// Always-visible date-range picker for list views.
//
// This started life inside /payouts's collapsed "Filters" panel, which was a
// mistake: a date range is not a power-user refinement on a payout history,
// it is the primary way an operator asks the page a question ("what went out
// on the 9th"). Behind a toggle it may as well not exist — the page still
// looks undated on arrival, which is the exact complaint the batch-header
// date was meant to fix.
//
// So it lives in the toolbar, open, with presets. The presets are the part
// that actually gets used: "today" and "last 7 days" are most of the real
// traffic, and typing two dates to answer them is friction that pushes people
// back to exporting CSVs.
import { useMemo } from "react";

import { Segmented } from "@/components/UI";
import {
  isReversedRange,
  listPresets,
  ymdLocal,
  type DateRangePreset,
} from "@/utils/dateRange";

export interface DateRangeFilterProps {
  from: string;
  to: string;
  onChange: (next: { from: string; to: string }) => void;
  /** Optional label shown before the controls. */
  label?: string;
  /** Preset chips; defaults to listPresets(). */
  presets?: DateRangePreset[];
}

export function DateRangeFilter({
  from,
  to,
  onChange,
  label = "Dates",
  presets: presetsProp,
}: DateRangeFilterProps) {
  const today = useMemo(() => ymdLocal(new Date()), []);
  const defaultPresets = useMemo(() => listPresets(), []);
  const presets = presetsProp ?? defaultPresets;
  const reversed = isReversedRange(from, to);

  // A preset is "selected" when the inputs already hold exactly its range, so
  // the operator can see which question the page is currently answering
  // rather than having to read two date boxes and do the arithmetic.
  //
  // Rendered through Segmented, NOT `btn ghost active`: the stylesheet has no
  // `.btn.active` rule, so that spelling produces a control whose selected
  // state is invisible — which on a date filter means the page silently shows
  // a window the operator can't see they chose.
  // "custom" when the dates match no preset — typed by hand, or a preset the
  // clock has since moved past. It is a real state and gets its own chip
  // rather than leaving every preset unselected, which reads as a bug.
  const activePreset =
    presets.find((p) => p.from === from && p.to === to)?.value ?? "custom";
  const options = presets.map(({ value, label }) => ({ value, label }));
  if (activePreset === "custom") {
    options.push({ value: "custom", label: "Custom" });
  }

  return (
    <div
      className="row gap-8"
      style={{ flexWrap: "wrap", alignItems: "center" }}
    >
      <span className="dim" style={{ fontSize: 11 }}>
        {label}
      </span>

      <Segmented
        value={activePreset}
        onChange={(v) => {
          // "Custom" is a readout of the current hand-typed range, not a
          // destination — clicking it must not wipe the dates it describes.
          if (v === "custom") return;
          const p = presets.find((x) => x.value === v);
          if (p) onChange({ from: p.from, to: p.to });
        }}
        options={options}
        label="Date range preset"
      />

      <label className="row gap-8" style={{ alignItems: "center" }}>
        <span className="dim" style={{ fontSize: 11 }}>
          From
        </span>
        <input
          className="input"
          style={{ maxWidth: 150 }}
          type="date"
          value={from}
          // max/min cross-bound each other so the native picker greys out
          // impossible days instead of letting the operator build a range
          // that can never match.
          max={to || today}
          onChange={(e) => onChange({ from: e.target.value, to })}
          aria-label="Show payouts created on or after"
        />
      </label>
      <label className="row gap-8" style={{ alignItems: "center" }}>
        <span className="dim" style={{ fontSize: 11 }}>
          To
        </span>
        <input
          className="input"
          style={{ maxWidth: 150 }}
          type="date"
          value={to}
          min={from || undefined}
          onChange={(e) => onChange({ from, to: e.target.value })}
          aria-label="Show payouts created on or before"
        />
      </label>

      {/* A typed date bypasses the min/max above. A reversed range matches
          nothing, which renders identically to "this merchant has no
          payouts" — so say which one it is. */}
      {reversed && (
        <span style={{ fontSize: 11, color: "var(--warn, #b42318)" }}>
          From is after To — no rows can match
        </span>
      )}
    </div>
  );
}
